/* ============================================================================
   FRONT LINE DEV - DO NOT EDIT
   This file powers Infinit's custom Quiz and Custom Formula functionality and
   is maintained exclusively by Front Line Dev. Editing it may break the
   formula builder, quiz, saved formulas, or related features. Please contact
   Front Line Dev for any changes.
   ============================================================================ */

/**
 * Quiz Results Page Populator
 *
 * Runs on /products/quiz-results. Reads scored quiz results from InfinitSession
 * and populates the product page sections with personalized formula data.
 */
(function () {
  'use strict';

  console.log('[quiz-results-page] Script loaded. readyState:', document.readyState);

  /** Convert a string to a Shopify-style handle */
  function handleize(str) {
    return (str || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /** Lowercase key preserving spaces for CSS swatch attribute selectors */
  function swatchKey(str) {
    return (str || '').toString().trim().toLowerCase();
  }

  /** Build flavor swatch HTML */
  function flavorHTML(flavorName) {
    var swatch = swatchKey(flavorName);
    var gradient = handleize(flavorName);
    return '<div class="flex items-center gap-3 mt-4">' +
      '<label class="opt-label opt-label--swatch relative" data-swatch="' + swatch + '" aria-hidden="true"></label>' +
      '<span class="h5 mb-0 has-flavor-gradient ring-gradient--' + gradient + ' product-subtitle">' + flavorName + '</span>' +
      '</div>';
  }

  /** Build compact flavor for the-plan cards */
  function planFlavorHTML(flavorName) {
    var swatch = swatchKey(flavorName);
    return '<label class="opt-label opt-label--swatch relative" data-swatch="' + swatch + '" aria-hidden="true"></label>' +
      '<span class="h6 mb-0">' + flavorName + '</span>';
  }

  /** Build results description HTML from resultsText array */
  function resultsDescHTML(resultsText) {
    if (!resultsText || !resultsText.length) return '';
    return '<div class="rte"><ul>' + resultsText.map(function (t) { return '<li>' + t + '</li>'; }).join('') + '</ul></div>';
  }

  /** Build per-formula description HTML, replacing {{tokens}} with actual nutrition values */
  function formulaDescHTML(textArr, apiData) {
    if (!textArr || !textArr.length) return '';
    var bullets = textArr.map(function (t) {
      if (apiData) {
        t = t.replace(/\{\{calories\}\}/g, apiData.calories != null ? String(apiData.calories) : '—');
        t = t.replace(/\{\{sodium\}\}/g, apiData.sodium != null ? String(apiData.sodium) : '—');
        t = t.replace(/\{\{protein\}\}/g, apiData.protein != null ? String(apiData.protein) : '—');
        t = t.replace(/\{\{caffeine\}\}/g, apiData.caffeine != null ? String(apiData.caffeine) : '—');
        t = t.replace(/\{\{carbs\}\}/g, apiData.carbs != null ? String(apiData.carbs) : '—');
        t = t.replace(/\{\{bcaas\}\}/g, apiData.bcaas != null ? String(apiData.bcaas) : '—');
      }
      return '<li>' + t + '</li>';
    });
    return '<div class="rte"><ul>' + bullets.join('') + '</ul></div>';
  }

  /**
   * Apply boost checkbox state (check recommended, hide others) within a section.
   * Waits for the boost snippet's infinit:boosts:rendered event, then applies state.
   * Re-applies on subsequent re-renders via event listener.
   */
  function applyBoostsScoped(section, boostIds) {
    if (!section) return;
    var role = section.getAttribute('data-formula-role') || 'unknown';
    var hasRecommended = boostIds && boostIds.length > 0;
    var stringIds = hasRecommended ? boostIds.map(function (id) { return String(id); }) : [];

    console.log('[quiz-results-page] applyBoostsScoped called for', role, '| boost IDs:', stringIds);

    function hideBoostArea() {
      var boostWrapper = section.querySelector('[data-ch-boosts]');
      if (!boostWrapper) return;
      boostWrapper.style.display = 'none';
      var prev = boostWrapper.previousElementSibling;
      if (prev && prev.textContent.indexOf('Boost Your Blend') !== -1) {
        prev.style.display = 'none';
      }
    }

    function applyState() {
      var checkboxes = section.querySelectorAll('input[name="boosts[]"]');
      if (checkboxes.length === 0) return false;

      console.log('[quiz-results-page] Applying boost state for', role, '| checkboxes:', checkboxes.length, '| recommended:', stringIds);

      var anyChecked = false;
      for (var i = 0; i < checkboxes.length; i++) {
        var li = checkboxes[i].closest('li');
        if (!li) continue;

        if (stringIds.indexOf(checkboxes[i].value) !== -1) {
          checkboxes[i].checked = true;
          checkboxes[i].disabled = true;
          anyChecked = true;
          li.setAttribute('data-quiz-boost', 'recommended');
          li.style.display = '';
        } else {
          li.setAttribute('data-quiz-boost', 'hidden');
          li.style.display = 'none';
        }
      }

      if (anyChecked) {
        checkboxes[0].dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        console.warn('[quiz-results-page] NO boosts matched in', role,
          '| wanted:', stringIds,
          '| available:', Array.prototype.map.call(checkboxes, function (cb) { return cb.value; }));
      }

      return true;
    }

    // Wait for boosts to render, then apply state
    waitForBoosts(section).then(function () {
      if (!hasRecommended) {
        hideBoostArea();
        return;
      }

      applyState();

      // Watch for re-renders (e.g., package size change triggers boost re-fetch).
      // Remove any previous handler to prevent listener stacking.
      if (section._boostReRenderHandler) {
        section.removeEventListener('infinit:boosts:rendered', section._boostReRenderHandler);
      }
      section._boostReRenderHandler = function () { applyState(); };
      section.addEventListener('infinit:boosts:rendered', section._boostReRenderHandler);
    });
  }

  /** Get boost names from checked checkboxes in a section */
  function getCheckedBoostNames(section) {
    var names = [];
    var checkboxes = section.querySelectorAll('input[name="boosts[]"]:checked');
    for (var i = 0; i < checkboxes.length; i++) {
      var cb = checkboxes[i];
      // Prefer data-label attribute (set by boost renderers at creation time)
      if (cb.dataset && cb.dataset.label) {
        names.push(cb.dataset.label);
        continue;
      }
      // Fallback: find the sibling <label> element
      var row = cb.closest('.option-selector') || cb.parentElement;
      if (row) {
        var labelEl = row.querySelector('label');
        if (labelEl) {
          var text = labelEl.textContent.trim();
          if (text) { names.push(text); continue; }
        }
      }
    }
    return names;
  }

  /**
   * Returns a Promise that resolves when boost checkboxes in the given section
   * are rendered and ready. Resolves immediately if they already exist.
   * Falls back to a 10s timeout to prevent indefinite hanging.
   */
  function waitForBoosts(section) {
    return new Promise(function (resolve) {
      // Already rendered? Resolve immediately.
      var existing = section.querySelectorAll('input[name="boosts[]"]');
      if (existing.length > 0) {
        resolve();
        return;
      }

      // No boost block in this section? Resolve immediately.
      var boostWrapper = section.querySelector('[data-ch-boosts]');
      if (!boostWrapper) {
        resolve();
        return;
      }

      // Listen for the event (bubbles up from boost snippet's root)
      var resolved = false;
      function onRendered() {
        if (resolved) return;
        resolved = true;
        section.removeEventListener('infinit:boosts:rendered', onRendered);
        resolve();
      }
      section.addEventListener('infinit:boosts:rendered', onRendered);

      // Safety timeout — don't hang forever if event never fires
      setTimeout(function () {
        if (!resolved) {
          resolved = true;
          section.removeEventListener('infinit:boosts:rendered', onRendered);
          console.warn('[quiz-results-page] waitForBoosts timed out for', section.getAttribute('data-formula-role'));
          resolve();
        }
      }, 10000);
    });
  }

  // ── Pricing ────────────────────────────────────────────────────────

  var HYDRATION_ENDPOINT = '/apps/formulas?route=hydration-nutrition';
  var PROTEIN_ENDPOINT = '/apps/formulas?route=protein-nutrition';

  function formatMoneyCents(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(n);
    }
    return '$' + (n / 100).toFixed(2);
  }

  function formatMoneyWithCurrencyCents(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      var fmt = window.Shopify.money_with_currency_format || window.Shopify.money_format;
      if (fmt) return window.Shopify.formatMoney(n, fmt);
      return window.Shopify.formatMoney(n);
    }
    return '$' + (n / 100).toFixed(2) + ' USD';
  }

  function parseMoneyTextToCents(str) {
    var s = String(str || '').trim();
    if (!s) return 0;
    var m = s.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
    if (!m) return 0;
    var n = Number(m[0]);
    return isFinite(n) ? Math.round(n * 100) : 0;
  }

  function toCentsInt(v) {
    if (v == null) return 0;
    if (typeof v === 'number') return isFinite(v) ? Math.round(v) : 0;
    var s = String(v).trim();
    if (!s) return 0;
    if (/[.$]/.test(s)) return parseMoneyTextToCents(s);
    var n = Number(s);
    return isFinite(n) ? Math.round(n) : 0;
  }

  function setSkeletonText(el, text) {
    if (!el) return;
    el.textContent = text;
    el.classList.remove('is-skeleton');
    el.style.display = '';
  }

  /** Read selected package size from a variant-picker (works for both radio and dropdown styles) */
  function getSelectedPackageSize(section) {
    // Radio-button style
    var radio = section.querySelector('variant-picker input.js-option:checked');
    if (radio) {
      var m = radio.value.match(/(\d+)/);
      if (m) return m[1];
    }
    // Dropdown (custom-select) style — read from the selected list item
    var selectedLi = section.querySelector('variant-picker .custom-select__option[aria-selected="true"]');
    if (selectedLi && selectedLi.dataset.value) {
      var m2 = selectedLi.dataset.value.match(/(\d+)/);
      if (m2) return m2[1];
    }
    // Fallback: read from dropdown button text
    var btn = section.querySelector('variant-picker .custom-select__btn');
    if (btn) {
      var m3 = btn.textContent.trim().match(/(\d+)/);
      if (m3) return m3[1];
    }
    return '25';
  }

  /** Build nutrition endpoint payload from formula config (scoped to a section) */
  function buildNutritionPayload(formula, section) {
    var formData = new FormData();
    var form = section.querySelector('form.js-product-form');

    // Get variant price from the form
    var variantInput = form ? form.querySelector('input[name="id"]') : null;
    var variantId = variantInput ? variantInput.value : '';

    // Read package size from the variant-picker's selected option
    var packageSize = getSelectedPackageSize(section);

    if (formula.type === 'h') {
      formData.set('flavor', formula.flavor || '');
      formData.set('flavor_intensity', String(formula.flavor_slider || 50));
      formData.set('calories', String(formula.calories_slider || 50));
      formData.set('carbohydrates', String(formula.carbs_slider || 50));
      formData.set('electrolytes', String(formula.electrolytes_slider || 50));
      formData.set('protein', String(formula.protein_slider || 0));
      formData.set('bcaas', String(formula.aminoacids_slider || 0));
      formData.set('caffeine', String(formula.caffeine_slider || 0));
      formData.set('selected_protein', formula.protein_type || 'None');
      formData.set('package_size', packageSize);
    } else {
      formData.set('flavor', formula.flavor || '');
      formData.set('flavor_intensity', String(formula.flavor_slider || 50));
      formData.set('calorie_carbs', String(formula.calorie_carbs_slider || 50));
      formData.set('protein', formula.protein_type || 'Whey');
      formData.set('package_size', packageSize);
    }

    // Collect currently checked boosts from the section (user may have toggled)
    var checkedBoosts = section.querySelectorAll('input[name="boosts[]"]:checked');
    var boostValues = [];
    for (var i = 0; i < checkedBoosts.length; i++) {
      if (checkedBoosts[i].value) {
        formData.append('boosts[]', checkedBoosts[i].value);
        boostValues.push(checkedBoosts[i].value);
      }
    }

    // Include base variant price from Liquid-injected data attribute (NOT from DOM text,
    // which shows the final calculated price and would cause a feedback loop)
    var basePriceCents = section.getAttribute('data-base-price-cents');
    if (basePriceCents) {
      formData.set('price_cents', basePriceCents);
    }

    console.log('[quiz-results-page] buildNutritionPayload:', {
      type: formula.type, role: section.getAttribute('data-formula-role'),
      price_cents: basePriceCents, boosts: boostValues
    });

    return formData;
  }

  /** Fetch the calculated price for a formula from the nutrition endpoint */
  function fetchFormulaPrice(formula, section, role) {
    var endpoint = (formula.type === 'p') ? PROTEIN_ENDPOINT : HYDRATION_ENDPOINT;
    var formData = buildNutritionPayload(formula, section);

    return fetch(endpoint, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin'
    })
    .then(function (resp) {
      if (!resp.ok) throw new Error('Pricing error (' + resp.status + ')');
      return resp.json();
    })
    .then(function (data) {
      if (!data || data.ok === false) return null;
      return data;
    })
    .catch(function (err) {
      console.error('[quiz-results-page] Price fetch failed for ' + role + ':', err);
      return null;
    });
  }

  /** Update the price display in a section and its plan card */
  function updateSectionPrice(section, data, role) {
    if (!data) return 0;

    var finalCents = toCentsInt(data.final_price_cents);
    if (finalCents <= 0) return 0;

    var priceText = formatMoneyCents(finalCents);

    // Update the section's product price
    var priceRoot = section.querySelector('.js-product-price');
    if (priceRoot) {
      var inner =
        priceRoot.querySelector('.price-item--regular') ||
        priceRoot.querySelector('.price-item') ||
        priceRoot.querySelector('.price__current') ||
        priceRoot.querySelector('.money');
      if (inner) inner.textContent = priceText;
      else priceRoot.textContent = priceText;
    }

    // Update subscription widget prices within this section.
    // The widget is a third-party app that may not have rendered yet.
    var boostsCents = toCentsInt(data.boosts_price_cents);
    var subBlock = section.querySelector('section.shopify_subscriptions_app_block');
    if (subBlock) {
      updateSubscriptionWidgetInSection(subBlock, finalCents, boostsCents);
    } else if (!section._subWidgetObserver) {
      // Store latest values on the section so observer reads fresh data
      section._latestPriceCents = finalCents;
      section._latestBoostsCents = boostsCents;
      section._subWidgetObserver = new MutationObserver(function () {
        var block = section.querySelector('section.shopify_subscriptions_app_block');
        if (block) {
          section._subWidgetObserver.disconnect();
          section._subWidgetObserver = null;
          updateSubscriptionWidgetInSection(block, section._latestPriceCents || 0, section._latestBoostsCents || 0);
        }
      });
      section._subWidgetObserver.observe(section, { childList: true, subtree: true });
    } else {
      // Observer already watching — update stored values so it uses latest when it fires
      section._latestPriceCents = finalCents;
      section._latestBoostsCents = boostsCents;
    }

    // Update the plan card price + cost per hour
    var planSection = document.querySelector('[data-formula-role="the-plan"]');
    if (planSection) {
      var card = planSection.querySelector('[data-plan-card="' + role + '"]');
      if (card) {
        var planPriceEl = card.querySelector('[data-plan-price]');
        setSkeletonText(planPriceEl, priceText);

        var costPerHourEl = card.querySelector('[data-plan-cost-per-hour]');
        if (costPerHourEl) {
          if (data.cost_per_hour_formatted) {
            setSkeletonText(costPerHourEl, data.cost_per_hour_formatted + ' / hour of fuel');
          } else if (typeof data.cost_per_hour_cents === 'number') {
            setSkeletonText(costPerHourEl, formatMoneyCents(data.cost_per_hour_cents) + ' / hour of fuel');
          } else if (data.cost_per_serving_formatted) {
            setSkeletonText(costPerHourEl, data.cost_per_serving_formatted + ' / serving');
          } else {
            // Calculate: price / servings
            var servings = data.package_size || data.servings || 25;
            var costPer = Math.round(finalCents / servings);
            var unit = (role === 'protein') ? ' / serving' : ' / hour of fuel';
            setSkeletonText(costPerHourEl, formatMoneyCents(costPer) + unit);
          }
        }
      }
    }

    return finalCents;
  }

  /** Find the visible price element near a radio input.
   *  Tries both the current Shopify Subscriptions app class names and legacy ones. */
  function findPriceElNearInput(input, container) {
    // Walk up to the closest row/wrapper, then find the price span
    var row = input.closest('.title_and_price_wrapper') ||
              input.closest('.group_list_item') ||
              input.closest('.shopify_subscriptions_purchase_option_wrapper');
    if (row) {
      var el = row.querySelector('.in_widget_price') ||
               row.querySelector('.shopify_subscriptions_in_widget_price');
      if (el) return el;
    }
    // Fallback: search within container
    if (container) {
      return container.querySelector('.in_widget_price') ||
             container.querySelector('.shopify_subscriptions_in_widget_price');
    }
    return null;
  }

  /** Apply custom prices to all elements within a subscription widget block */
  function applyWidgetPrices(block, oneTimeCents) {
    var oneTimeText = formatMoneyWithCurrencyCents(oneTimeCents);
    if (!oneTimeText) {
      console.warn('[quiz-results-page] applyWidgetPrices: empty oneTimeText for', oneTimeCents);
      return;
    }

    console.log('[quiz-results-page] applyWidgetPrices: oneTimeCents=', oneTimeCents, 'formatted=', oneTimeText);

    // One-time purchase line
    var oneTimeInput = block.querySelector('input[data-radio-type="one_time_purchase"]');
    if (oneTimeInput) {
      oneTimeInput.setAttribute('data-variant-price', oneTimeText);
      // Find the visible price element near this input.
      // One-time structure: <label class="title_and_price_wrapper"> wraps both
      // <div class="group_list_item"> (contains input) and <div class="in_widget_price"> (sibling).
      // So we must go up to title_and_price_wrapper first, not group_list_item.
      var oneTimeRow = oneTimeInput.closest('.title_and_price_wrapper') ||
                       oneTimeInput.closest('.subscription_group') ||
                       oneTimeInput.closest('.shopify_subscriptions_purchase_option_wrapper') ||
                       block;
      var oneTimePriceEl = oneTimeRow.querySelector('.in_widget_price') ||
                           oneTimeRow.querySelector('.shopify_subscriptions_in_widget_price');
      console.log('[quiz-results-page] applyWidgetPrices: oneTimePriceEl=', !!oneTimePriceEl, 'before:', oneTimePriceEl ? oneTimePriceEl.textContent : 'N/A');
      if (oneTimePriceEl) oneTimePriceEl.textContent = oneTimeText;
    }

    // Selling plan inputs — update data attributes AND visible price text
    var planInputs = block.querySelectorAll('input[data-radio-type="selling_plan"][data-selling-plan-id]');
    console.log('[quiz-results-page] applyWidgetPrices: planInputs count=', planInputs.length);
    for (var i = 0; i < planInputs.length; i++) {
      var inp = planInputs[i];

      // Discount factor: default 10% off unless widget has real ratios
      var origPrice = parseMoneyTextToCents(inp.getAttribute('data-variant-price') || '');
      var origCompare = parseMoneyTextToCents(inp.getAttribute('data-variant-compare-at-price') || '');
      var factor = (origCompare > 0 && origPrice > 0) ? (origPrice / origCompare) : 0.9;

      var planCents = Math.round(oneTimeCents * factor);
      var planText = formatMoneyWithCurrencyCents(planCents);

      inp.setAttribute('data-variant-price', planText);
      inp.setAttribute('data-variant-compare-at-price', oneTimeText);

      // Update the visible price label next to this selling plan option
      var planRow = inp.closest('.group_list_item') ||
                    inp.closest('.title_and_price_wrapper') ||
                    inp.closest('.shopify_subscriptions_purchase_option_wrapper');
      if (planRow) {
        var priceEl = planRow.querySelector('.in_widget_price') ||
                      planRow.querySelector('.shopify_subscriptions_in_widget_price');
        if (priceEl) priceEl.textContent = planText;
      }
    }

    // Allocation price header (shown at top of widget when subscription is selected)
    var allocationEl = block.querySelector('.in_widget_price.allocation_price') ||
                       block.querySelector('.shopify_subscriptions_in_widget_price.allocation_price');
    if (allocationEl) {
      var selected = block.querySelector('input[data-radio-type="selling_plan"]:checked') || planInputs[0];
      if (selected) allocationEl.textContent = selected.getAttribute('data-variant-price') || allocationEl.textContent;
    }
  }

  /** Update Shopify subscription widget prices within a section.
   *  Re-applies after the third-party subscription app overwrites our prices. */
  function updateSubscriptionWidgetInSection(block, oneTimeCents, boostCents) {
    if (!block) return;
    if (oneTimeCents <= 0) return;

    // Store the target price on the block so re-apply and interval use the latest value
    block._customOneTimeCents = oneTimeCents;

    applyWidgetPrices(block, oneTimeCents);

    // The Shopify subscription app may initialize AFTER our update and overwrite
    // prices with the product's $0 variant price. Use both MutationObserver and
    // a timed fallback to guarantee prices get applied.
    if (!block._priceGuardObserver) {
      var reapplyCount = 0;
      var lastApplyTime = Date.now();

      block._priceGuardObserver = new MutationObserver(function () {
        if (Date.now() - lastApplyTime < 200) return;
        if (reapplyCount >= 5) return;

        var storedCents = block._customOneTimeCents || 0;
        if (storedCents <= 0) return;

        reapplyCount++;
        lastApplyTime = Date.now();
        console.log('[quiz-results-page] Re-applying widget prices (mutation detected), attempt', reapplyCount);
        applyWidgetPrices(block, storedCents);
      });

      block._priceGuardObserver.observe(block, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['data-variant-price', 'data-variant-compare-at-price']
      });

      // Fallback: re-apply on a schedule to defeat subscription app re-renders.
      // Runs at 1s, 2s, 4s, 6s, 10s after first call — covers async app init.
      var delays = [1000, 2000, 4000, 6000, 10000];
      delays.forEach(function (delay) {
        setTimeout(function () {
          var storedCents = block._customOneTimeCents || 0;
          if (storedCents <= 0) return;
          // Check if prices are still wrong
          var oneTimeInput = block.querySelector('input[data-radio-type="one_time_purchase"]');
          if (!oneTimeInput) return;
          var currentPrice = parseMoneyTextToCents(oneTimeInput.getAttribute('data-variant-price') || '');
          if (currentPrice !== storedCents) {
            console.log('[quiz-results-page] Interval re-apply at', delay, 'ms: current=', currentPrice, 'target=', storedCents);
            lastApplyTime = Date.now();
            applyWidgetPrices(block, storedCents);
          }
        }, delay);
      });
    }
  }

  // Store for prices, servings, and last API data so we can compute plan total and per-unit costs
  var sectionPrices = {};
  var sectionServings = {};
  var sectionLastData = {};  // last API response per role, for re-applying subscription discount
  var sectionPriceUpdating = {};  // role → boolean, true while a refreshSectionPrice fetch is in-flight
  var includeHydrationB = true; // set based on is3Plus during init

  /** Get the subscription discount factor from a section's subscription widget */
  function getSubscriptionDiscountFactor(section) {
    var subBlock = section.querySelector('section.shopify_subscriptions_app_block');
    if (!subBlock) return 1;
    var selectedInput = subBlock.querySelector('input[data-radio-type="selling_plan"]:checked');
    if (!selectedInput) return 1;
    var origPrice = parseMoneyTextToCents(selectedInput.getAttribute('data-variant-compare-at-price') || '');
    var discPrice = parseMoneyTextToCents(selectedInput.getAttribute('data-variant-price') || '');
    if (origPrice > 0 && discPrice > 0) return discPrice / origPrice;
    return 0.9; // default 10% discount
  }

  /** Fetch and update price for a single formula section */
  function refreshSectionPrice(section, formula, role) {
    return fetchFormulaPrice(formula, section, role).then(function (data) {
      console.log('[quiz-results-page] refreshSectionPrice response for', role, ':', data ? JSON.stringify(data).slice(0, 200) : 'null');
      var cents = updateSectionPrice(section, data, role);
      if (data) {
        sectionLastData[role] = data;
        // Re-render description with actual nutrition values
        var descEl = section.querySelector('[data-quiz-formula-desc]');
        if (descEl && window._quizResults) {
          var descText = null;
          var r = window._quizResults;
          if (role === 'hydrationA') descText = r.hydrationAText;
          else if (role === 'hydrationB') descText = r.hydrationBText;
          else if (role === 'protein') descText = r.proteinText;
          if (descText) {
            descEl.innerHTML = formulaDescHTML(descText, data);
          }
        }
        updateSectionCostPerUnit(section, data, role);
        updatePlanCardStats(data, role);
        // Populate Nutrition Facts panel if present
        if (data.nutrition_html) {
          var nutritionPanel = section.querySelector('[data-nutrition-panel]');
          if (nutritionPanel) nutritionPanel.innerHTML = data.nutrition_html;
          // Also populate the plan card's side-drawer nutrition panel
          var planNutritionPanel = document.querySelector('[data-plan-nutrition-panel="' + role + '"]');
          console.log('[quiz-results-page] Nutrition panel for', role, '| found:', !!planNutritionPanel, '| html length:', data.nutrition_html.length);
          if (planNutritionPanel) planNutritionPanel.innerHTML = '<div class="rte">' + data.nutrition_html + '</div>';
        } else {
          console.warn('[quiz-results-page] No nutrition_html in response for', role);
        }
      }
      sectionPrices[role] = cents;
      updatePlanTotalPrice();
      updatePlanSubscriptionWidget();
      return cents;
    });
  }

  /** Update the plan section's total price from all formula prices */
  function updatePlanTotalPrice() {
    var planPriceEl = document.querySelector('[data-plan-total-price]');
    if (!planPriceEl) return;

    var requiredRoles = ['hydrationA', 'protein'];
    if (includeHydrationB) requiredRoles.push('hydrationB');

    var allReady = requiredRoles.every(function (role) {
      return typeof sectionPrices[role] === 'number' && sectionPrices[role] > 0;
    });

    if (!allReady) return;

    var total = 0;
    requiredRoles.forEach(function (role) {
      total += sectionPrices[role] || 0;
    });

    var inner =
      planPriceEl.querySelector('.price-item--regular') ||
      planPriceEl.querySelector('.price-item') ||
      planPriceEl.querySelector('.price__current') ||
      planPriceEl.querySelector('.money');

    var text = formatMoneyCents(total);
    if (inner) inner.textContent = text;
    else planPriceEl.textContent = text;

    planPriceEl.classList.remove('is-skeleton');
  }

  /** Update the cost/hour (or cost/serving) display within a formula section */
  function updateSectionCostPerUnit(section, data, role) {
    var el = section.querySelector('.product-serving-count .rte');
    if (!el) return;

    var finalCents = toCentsInt(data.final_price_cents);
    var servings = data.package_size || data.servings || 25;
    var unit = (role === 'protein') ? ' / serving' : ' / hour of fuel';

    // Store servings for plan subscription calculations
    sectionServings[role] = servings;

    // Determine cost per unit from API response or calculate it
    var costPerUnit;
    if (typeof data.cost_per_hour_cents === 'number' && role !== 'protein') {
      costPerUnit = data.cost_per_hour_cents;
    } else if (typeof data.cost_per_serving_cents === 'number') {
      costPerUnit = data.cost_per_serving_cents;
    } else {
      costPerUnit = servings > 0 ? Math.round(finalCents / servings) : 0;
    }

    // Apply subscription discount if subscription is selected.
    // Check both the hidden selling_plan input AND the visual radio buttons,
    // as the Shopify subscription widget uses both.
    var sellingPlanInput = section.querySelector('input[name="selling_plan"]') ||
                           section.querySelector('select[name="selling_plan"]');
    var sellingPlanValue = sellingPlanInput ? String(sellingPlanInput.value || '').trim() : '';
    var hasSellingPlan = /^\d+$/.test(sellingPlanValue) && sellingPlanValue !== '0';

    // Also check radio buttons as fallback
    if (!hasSellingPlan) {
      var subRadio = section.querySelector('input[data-radio-type="selling_plan"]:checked');
      var isOneTime = section.querySelector('input[data-radio-type="one_time_purchase"]:checked');
      hasSellingPlan = !!(subRadio && !isOneTime);
    }

    if (hasSellingPlan) {
      var factor = getSubscriptionDiscountFactor(section);
      costPerUnit = Math.round(costPerUnit * factor);
    }

    el.textContent = formatMoneyCents(costPerUnit) + unit;
  }

  /** Update the plan card nutrition stats from API response data */
  function updatePlanCardStats(data, role) {
    if (!data) return;
    var planSection = document.querySelector('[data-formula-role="the-plan"]');
    if (!planSection) return;
    var card = planSection.querySelector('[data-plan-card="' + role + '"]');
    if (!card) return;
    var statsEl = card.querySelector('[data-plan-stats]');
    if (!statsEl) return;

    // Find all label/value pairs
    var labels = statsEl.querySelectorAll('.quiz-result-plan_product-nutrition-label');
    for (var i = 0; i < labels.length; i++) {
      var label = labels[i].textContent.trim().toLowerCase();
      var valueEl = labels[i].parentElement.querySelector('.quiz-result-plan_product-nutrition-value');
      if (!valueEl) continue;

      // Get the unit suffix span (mg, g) if present
      var unitSpan = valueEl.querySelector('.text-sm');
      var unitText = unitSpan ? unitSpan.outerHTML : '';

      var val = null;
      if (label === 'calories' && data.calories != null) {
        val = String(Math.round(Number(data.calories)));
        unitText = ''; // calories has no unit suffix
      } else if (label === 'sodium' && data.sodium != null) {
        val = String(Math.round(Number(data.sodium)));
      } else if (label === 'carbs' && data.carbs != null) {
        val = String(Math.round(Number(data.carbs)));
      } else if (label === 'protein' && data.protein != null) {
        val = String(Math.round(Number(data.protein)));
      } else if (label === 'bcaas' && data.bcaas != null) {
        val = String(Math.round(Number(data.bcaas)));
      }

      if (val !== null) {
        valueEl.innerHTML = val + unitText;
        valueEl.classList.remove('is-skeleton');
      }
    }
  }

  /** Update the plan section's subscription widget with the total price.
   *  If the widget hasn't rendered yet (third-party app, async), observe for it. */
  function updatePlanSubscriptionWidget() {
    var planSection = document.querySelector('[data-formula-role="the-plan"]');
    if (!planSection) {
      console.warn('[quiz-results-page] updatePlanSubscriptionWidget: no plan section found');
      return;
    }

    var total = 0;
    for (var role in sectionPrices) {
      if (role === 'hydrationB' && !includeHydrationB) continue;
      total += (sectionPrices[role] || 0);
    }
    console.log('[quiz-results-page] updatePlanSubscriptionWidget: total=', total, 'sectionPrices=', JSON.stringify(sectionPrices));
    if (total <= 0) return;

    var subBlock = planSection.querySelector('section.shopify_subscriptions_app_block');
    console.log('[quiz-results-page] updatePlanSubscriptionWidget: subBlock found=', !!subBlock);
    if (subBlock) {
      console.log('[quiz-results-page] updatePlanSubscriptionWidget: calling updateSubscriptionWidgetInSection with total=', total);
      updateSubscriptionWidgetInSection(subBlock, total, 0);
      return;
    }

    // Subscription widget hasn't rendered yet — observe for it (once)
    if (planSection._subWidgetObserver) return; // already watching
    planSection._subWidgetObserver = new MutationObserver(function () {
      var block = planSection.querySelector('section.shopify_subscriptions_app_block');
      if (block) {
        planSection._subWidgetObserver.disconnect();
        planSection._subWidgetObserver = null;
        // Recalculate total — prices may have updated since observer was created
        var currentTotal = 0;
        for (var r in sectionPrices) {
          if (r === 'hydrationB' && !includeHydrationB) continue;
          currentTotal += (sectionPrices[r] || 0);
        }
        console.log('[quiz-results-page] Plan sub widget observer fired, total:', currentTotal);
        if (currentTotal > 0) {
          updateSubscriptionWidgetInSection(block, currentTotal, 0);
        }
      }
    });
    planSection._subWidgetObserver.observe(planSection, { childList: true, subtree: true });
  }

  /** DIAGNOSTIC: Dump subscription widget structure to console for debugging */
  function debugSubscriptionWidget() {
    var planSection = document.querySelector('[data-formula-role="the-plan"]');
    if (!planSection) {
      console.warn('[WIDGET-DEBUG] No plan section found');
      return;
    }

    // Check for the widget block
    var subBlock = planSection.querySelector('section.shopify_subscriptions_app_block');
    console.log('[WIDGET-DEBUG] section.shopify_subscriptions_app_block found:', !!subBlock);

    if (subBlock) {
      console.log('[WIDGET-DEBUG] Widget outer HTML (first 2000 chars):', subBlock.outerHTML.substring(0, 2000));
      console.log('[WIDGET-DEBUG] Widget data-variant-id:', subBlock.getAttribute('data-variant-id'));

      var oneTimeInput = subBlock.querySelector('input[data-radio-type="one_time_purchase"]');
      console.log('[WIDGET-DEBUG] One-time input found:', !!oneTimeInput);
      if (oneTimeInput) {
        console.log('[WIDGET-DEBUG] One-time data-variant-price:', oneTimeInput.getAttribute('data-variant-price'));
      }

      var planInputs = subBlock.querySelectorAll('input[data-radio-type="selling_plan"]');
      console.log('[WIDGET-DEBUG] Selling plan inputs found:', planInputs.length);

      var priceEls = subBlock.querySelectorAll('.shopify_subscriptions_in_widget_price');
      console.log('[WIDGET-DEBUG] Price elements found:', priceEls.length);
      for (var i = 0; i < priceEls.length; i++) {
        console.log('[WIDGET-DEBUG] Price el', i, ':', priceEls[i].textContent, '| classes:', priceEls[i].className);
      }

      var allInputs = subBlock.querySelectorAll('input');
      console.log('[WIDGET-DEBUG] All inputs in widget:', allInputs.length);
      for (var j = 0; j < allInputs.length; j++) {
        console.log('[WIDGET-DEBUG] Input', j, ':', allInputs[j].type, allInputs[j].name, '| data-radio-type:', allInputs[j].getAttribute('data-radio-type'), '| value:', allInputs[j].value);
      }
    } else {
      // Search more broadly
      var anySubBlock = planSection.querySelectorAll('[class*="shopify_subscriptions"]');
      console.log('[WIDGET-DEBUG] Elements with shopify_subscriptions class:', anySubBlock.length);
      for (var k = 0; k < anySubBlock.length; k++) {
        console.log('[WIDGET-DEBUG] Sub element', k, ':', anySubBlock[k].tagName, anySubBlock[k].className);
      }

      // Check for app block container
      var appBlocks = planSection.querySelectorAll('.product-info__block');
      console.log('[WIDGET-DEBUG] product-info__block elements:', appBlocks.length);
      for (var m = 0; m < appBlocks.length; m++) {
        var html = appBlocks[m].innerHTML.substring(0, 300);
        if (html.indexOf('subscription') !== -1 || html.indexOf('selling_plan') !== -1 || html.indexOf('shopify') !== -1) {
          console.log('[WIDGET-DEBUG] App block', m, 'contains subscription content:', html);
        }
      }
    }
  }

  // Run diagnostic at 3s and 8s after page load to capture async widget rendering
  setTimeout(debugSubscriptionWidget, 3000);
  setTimeout(debugSubscriptionWidget, 8000);

  /** Listen for subscription option changes in the plan section and update the total price */
  function wirePlanSubscriptionListener(planSection) {
    if (!planSection) return;

    planSection.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;

      var isSub = (t.name === 'selling_plan') ||
                  (t.matches && t.matches('input[data-radio-type]'));
      if (!isSub) return;

      console.log('[quiz-results-page] Plan subscription change:', t.name, t.value, t.dataset && t.dataset.radioType);

      // Calculate total from stored section prices
      var total = 0;
      for (var role in sectionPrices) {
        if (role === 'hydrationB' && !includeHydrationB) continue;
        total += (sectionPrices[role] || 0);
      }
      if (total <= 0) return;

      // Determine discount factor from the selected option
      var isOneTime = !t.value || t.value === '0' ||
                      (t.dataset && t.dataset.radioType === 'one_time_purchase');
      var displayTotal = total;

      if (!isOneTime) {
        // Get the discount factor from the subscription widget's data attributes
        var subBlock = planSection.querySelector('section.shopify_subscriptions_app_block');
        if (subBlock) {
          var selectedInput = subBlock.querySelector('input[data-radio-type="selling_plan"]:checked');
          if (selectedInput) {
            var origPrice = parseMoneyTextToCents(selectedInput.getAttribute('data-variant-compare-at-price') || '');
            var discPrice = parseMoneyTextToCents(selectedInput.getAttribute('data-variant-price') || '');
            if (origPrice > 0 && discPrice > 0) {
              var factor = discPrice / origPrice;
              displayTotal = Math.round(total * factor);
            } else {
              // Default 10% discount
              displayTotal = Math.round(total * 0.9);
            }
          } else {
            displayTotal = Math.round(total * 0.9);
          }
        }
      }

      // Update the plan total price display
      var planPriceEl = planSection.querySelector('[data-plan-total-price]');
      if (planPriceEl) {
        var text = formatMoneyCents(displayTotal);
        var inner =
          planPriceEl.querySelector('.price-item--regular') ||
          planPriceEl.querySelector('.price-item') ||
          planPriceEl.querySelector('.price__current') ||
          planPriceEl.querySelector('.money');
        if (inner) inner.textContent = text;
        else planPriceEl.textContent = text;
        planPriceEl.classList.remove('is-skeleton');
      }

      // Also determine the factor for per-card cost updates
      var cardFactor = isOneTime ? 1 : (displayTotal / total);

      // Update each plan card's cost per hour/serving
      ['hydrationA', 'hydrationB', 'protein'].forEach(function (cardRole) {
        if (cardRole === 'hydrationB' && !includeHydrationB) return;
        var card = planSection.querySelector('[data-plan-card="' + cardRole + '"]');
        var costEl = card && card.querySelector('[data-plan-cost-per-hour]');
        if (!costEl || !sectionPrices[cardRole]) return;
        var servings = sectionServings[cardRole] || 25;
        var discounted = Math.round(sectionPrices[cardRole] * cardFactor / servings);
        var unit = (cardRole === 'protein') ? ' / serving' : ' / hour of fuel';
        setSkeletonText(costEl, formatMoneyCents(discounted) + unit);
      });
    }, true);
  }

  /** Update the main price display in a section to reflect subscription discount if selected */
  function applySubscriptionDiscountToMainPrice(section) {
    var subBlock = section.querySelector('section.shopify_subscriptions_app_block');
    if (!subBlock) return;

    var selectedPlan = subBlock.querySelector('input[data-radio-type="selling_plan"]:checked');
    var isOneTime = subBlock.querySelector('input[data-radio-type="one_time_purchase"]:checked');

    var priceRoot = section.querySelector('.js-product-price');
    if (!priceRoot) return;

    var inner =
      priceRoot.querySelector('.price-item--regular') ||
      priceRoot.querySelector('.price-item') ||
      priceRoot.querySelector('.price__current') ||
      priceRoot.querySelector('.money');
    if (!inner) return;

    if (selectedPlan && !isOneTime) {
      // Subscription is selected — show discounted price
      var subPrice = selectedPlan.getAttribute('data-variant-price') || '';
      var subCents = parseMoneyTextToCents(subPrice);
      if (subCents > 0) {
        inner.textContent = formatMoneyCents(subCents);
      }
    }
    // If one-time is selected, refreshSectionPrice already set the correct one-time price
  }

  /** Set up listeners for boost/subscription changes within a section */
  function wirePriceListeners(section, formula, role) {
    section.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;

      var isBoost = (t.tagName === 'INPUT' && t.name === 'boosts[]');
      var isSub = (t.name === 'selling_plan') ||
                  t.matches('input[data-radio-type]');
      var isVariant = t.matches('input[name="id"]') ||
                      (t.closest && t.closest('variant-picker') && t.matches('.js-option'));

      if (isBoost || isSub || isVariant) {
        console.log('[quiz-results-page] wirePriceListeners: change detected in', role, '| target:', t.name || t.type, '| checked:', t.checked);

        if (isSub && !isBoost && !isVariant && sectionLastData[role]) {
          // Subscription toggle only — no need to re-fetch from API.
          // Re-apply cost-per-unit with current subscription state.
          if (section._priceTimer) clearTimeout(section._priceTimer);
          section._priceTimer = setTimeout(function () {
            updateSectionCostPerUnit(section, sectionLastData[role], role);
            applySubscriptionDiscountToMainPrice(section);
          }, 50);
        } else {
          // Boost or variant change — re-fetch price from API
          if (section._priceTimer) clearTimeout(section._priceTimer);
          var priceRole = section.getAttribute('data-formula-role') || '';
          sectionPriceUpdating[priceRole] = true;
          section._priceTimer = setTimeout(function () {
            refreshSectionPrice(section, formula, role).then(function () {
              applySubscriptionDiscountToMainPrice(section);
            }).finally(function () {
              sectionPriceUpdating[priceRole] = false;
            });
          }, 80);
        }
      }
    }, true);
  }

  // ── Custom Cart Submission ──────────────────────────────────────────

  var APP_PROXY_ENDPOINT = '/apps/formulas/add-to-cart';

  /** Build the payload for the app proxy add-to-cart endpoint.
   *  @param {string} [sellingPlanOverride] - Optional selling plan ID to use (e.g. from plan section widget) */
  function buildCartPayload(formula, formulaName, section, sellingPlanOverride) {
    var formData = new FormData();
    var form = section.querySelector('form.js-product-form');

    // Get variant ID from the form
    var variantInput = form ? form.querySelector('input[name="id"]') : null;
    var baseVariantId = variantInput ? variantInput.value : '';
    formData.set('base_variant_id', baseVariantId);
    formData.set('quantity', '1');

    // Mix name
    formData.set('properties[Blend name]', formulaName || '');

    // Flavor
    formData.set('properties[Flavor]', formula.flavor || '');
    formData.set('flavor', formula.flavor || '');
    formData.set('properties[Selected Flavor]', formula.flavor || '');

    if (formula.type === 'h') {
      // Hydration formula
      formData.set('formula_type', 'hydration');
      formData.set('properties[Formula Type]', 'Hydration');
      formData.set('flavor_intensity', String(formula.flavor_slider || 50));
      formData.set('properties[Flavor Intensity]', String(formula.flavor_slider || 50) + '%');

      // Send slider POSITIONS (0-100) for backend nutrition label generation.
      // The backend's $rawSliders reads from properties[*_slider] (priority 1) then
      // $data['*_slider'] (priority 2) — we set both so the backend can recompute
      // the nutrition label from raw positions regardless of which key it checks.
      formData.set('calories_slider', String(formula.calories_slider || 50));
      formData.set('carbs_slider', String(formula.carbs_slider || 50));
      formData.set('electrolytes_slider', String(formula.electrolytes_slider || 50));
      formData.set('protein_slider', String(formula.protein_slider || 0));
      formData.set('aminoacids_slider', String(formula.aminoacids_slider || 0));
      formData.set('caffeine_slider', String(formula.caffeine_slider || 0));
      formData.set('properties[calories_slider]', String(formula.calories_slider || 50));
      formData.set('properties[carbs_slider]', String(formula.carbs_slider || 50));
      formData.set('properties[electrolytes_slider]', String(formula.electrolytes_slider || 50));
      formData.set('properties[protein_slider]', String(formula.protein_slider || 0));
      formData.set('properties[aminoacids_slider]', String(formula.aminoacids_slider || 0));
      formData.set('properties[caffeine_slider]', String(formula.caffeine_slider || 0));

      formData.set('selected_protein', formula.protein_type || 'None');
      formData.set('properties[Selected Protein]', formula.protein_type || 'None');

      // For the top-level nutrition fields (calories, carbohydrates, etc.) AND
      // cart display properties[Calories], etc., send the actual COMPUTED nutrition
      // values from the cached price-fetch response (apiData). This mirrors what
      // sections/custom-hydration-sliders.liquid sends so the cart displays real
      // nutrition (e.g. "Calories: 287") rather than slider positions
      // (e.g. "Calories: 63"). If apiData is missing (price fetch hadn't completed
      // before submit), leave these fields empty and let the PHP backend's
      // getNutritionLabel() fill them from raw slider positions.
      var role = section.getAttribute('data-formula-role') || '';
      var apiData = sectionLastData[role];
      console.log('[quiz-results-page] buildCartPayload: role =', role, '| apiData =', apiData ? 'available' : 'MISSING', '| sectionLastData keys =', Object.keys(sectionLastData));
      if (apiData) {
        if (apiData.calories != null) {
          formData.set('calories', String(apiData.calories));
          formData.set('properties[Calories]', String(apiData.calories));
        }
        if (apiData.carbs != null) {
          formData.set('carbohydrates', String(apiData.carbs));
          formData.set('properties[Carbohydrates]', String(apiData.carbs));
        }
        if (apiData.sodium != null) {
          formData.set('electrolytes', String(apiData.sodium));
          formData.set('properties[Sodium]', String(apiData.sodium));
        }
        if (apiData.protein != null) {
          formData.set('protein', String(apiData.protein));
          formData.set('properties[Protein]', String(apiData.protein));
        }
        if (apiData.bcaas != null) {
          formData.set('bcaas', String(apiData.bcaas));
          formData.set('properties[BCAAs]', String(apiData.bcaas));
        }
        if (apiData.caffeine != null) {
          formData.set('caffeine', String(apiData.caffeine));
          formData.set('properties[Caffeine]', String(apiData.caffeine));
        }
      }
    } else {
      // Protein formula
      // Nullish coalescing — `||` would coerce a legitimate Low-Cal slider
      // position of 0 to 50 (Moderate), changing the customer's selection.
      var ccs = formula.calorie_carbs_slider ?? 50;
      formData.set('calorie_carbs', String(ccs));
      formData.set('calorie_level', String(ccs));
      formData.set('calorie_carbs_slider', String(ccs));
      formData.set('flavor_intensity', String(formula.flavor_slider || 50));
      formData.set('properties[Flavor Intensity]', String(formula.flavor_slider || 50) + '%');
      formData.set('selected_protein', formula.protein_type || 'Whey');
      formData.set('protein', formula.protein_type || 'Whey');
      formData.set('properties[Selected Protein]', formula.protein_type || 'Whey');
    }

    // Package size — read from variant-picker (radio or dropdown)
    formData.set('package_size', getSelectedPackageSize(section));

    // Boosts — read from currently checked checkboxes (user may have toggled)
    var checkedBoosts = section.querySelectorAll('input[name="boosts[]"]:checked');
    var boostIdsSent = [];
    for (var i = 0; i < checkedBoosts.length; i++) {
      if (checkedBoosts[i].value) {
        formData.append('boosts[]', checkedBoosts[i].value);
        boostIdsSent.push(checkedBoosts[i].value);
      }
    }

    // Fallback: if no checked checkboxes found but formula has boosts, use original quiz data
    if (boostIdsSent.length === 0 && formula.boosts && formula.boosts.length) {
      console.warn('[quiz-results-page] buildCartPayload: no checked boosts in DOM for', section.getAttribute('data-formula-role'), '— using formula.boosts fallback:', formula.boosts);
      for (var j = 0; j < formula.boosts.length; j++) {
        formData.append('boosts[]', String(formula.boosts[j]));
        boostIdsSent.push(String(formula.boosts[j]));
      }
    }

    // Also send boost IDs as a comma-separated property (PHP fallback path)
    if (boostIdsSent.length > 0) {
      formData.set('properties[Boost IDs]', boostIdsSent.join(','));
    }

    // Selling plan (subscription) — use override if provided (from plan section),
    // otherwise detect from the section's own subscription widget.
    if (sellingPlanOverride && sellingPlanOverride !== '0') {
      formData.set('selling_plan', sellingPlanOverride);
      console.log('[quiz-results-page] buildCartPayload: selling_plan from override =', sellingPlanOverride);
    } else {
      var subBlock = section.querySelector('section.shopify_subscriptions_app_block');
      if (subBlock) {
        var sellingPlanRadio = subBlock.querySelector('input[data-radio-type="selling_plan"][data-selling-plan-id]:checked');
        if (sellingPlanRadio) {
          var planVal = sellingPlanRadio.getAttribute('data-selling-plan-id') || sellingPlanRadio.value || '';
          if (planVal && planVal !== '0') {
            formData.set('selling_plan', planVal);
            console.log('[quiz-results-page] buildCartPayload: selling_plan =', planVal);
          }
        }
      } else {
        var hiddenPlan = section.querySelector('input[name="selling_plan"]:checked') ||
                         section.querySelector('select[name="selling_plan"]');
        if (hiddenPlan && hiddenPlan.value && hiddenPlan.value !== '0') {
          formData.set('selling_plan', hiddenPlan.value);
          console.log('[quiz-results-page] buildCartPayload: selling_plan from hidden input:', hiddenPlan.value);
        }
      }
    }

    return formData;
  }

  /** Submit a single formula to the app proxy and add to cart.
   *  @param {string} [sellingPlanOverride] - Optional selling plan ID override */
  function addFormulaToCart(formula, formulaName, section, sellingPlanOverride) {
    var formData = buildCartPayload(formula, formulaName, section, sellingPlanOverride);
    var role = section.getAttribute('data-formula-role') || 'unknown';

    // Log what we're sending
    var debugEntries = {};
    formData.forEach(function (v, k) { debugEntries[k] = v; });
    console.log('[quiz-results-page] addFormulaToCart for', role, '| formulaName:', formulaName, '| payload:', debugEntries);

    return fetch(APP_PROXY_ENDPOINT, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
    .then(function (resp) {
      if (!resp.ok) {
        return resp.text().then(function (txt) {
          console.error('[quiz-results-page] App proxy error for', role, ':', resp.status, txt);
          throw new Error('Server error (' + resp.status + '): ' + txt.slice(0, 200));
        });
      }
      return resp.json();
    })
    .then(function (data) {
      console.log('[quiz-results-page] App proxy response for', role, ':', JSON.stringify(data).slice(0, 300));
      if (!data.ok) throw new Error(data.message || 'Unable to create custom formula.');
      if (!data.variant_id) throw new Error('Missing variant ID from server.');

      var cartPayload = buildCartItemFromServerData(data, section, sellingPlanOverride);
      console.log('[quiz-results-page] Cart payload for', role, ':', JSON.stringify(cartPayload));

      return cartAddWithRetry({ items: [cartPayload] }, 0);
    })
    .then(function (cartResp) {
      if (!cartResp.ok) {
        return cartResp.text().then(function (txt) {
          console.error('[quiz-results-page] Cart error for', role, ':', cartResp.status, txt);
          throw new Error(parseCartError(cartResp.status, txt));
        });
      }
      return cartResp.json();
    });
  }

  /** Build a single cart line-item object from the app proxy response */
  function buildCartItemFromServerData(data, section, sellingPlanOverride) {
    var item = {
      id: Number(data.variant_id),
      quantity: data.quantity || 1,
      properties: data.properties || {}
    };
    if (data.selling_plan) {
      item.selling_plan = Number(data.selling_plan);
    } else if (sellingPlanOverride && sellingPlanOverride !== '0') {
      item.selling_plan = Number(sellingPlanOverride);
    } else if (section) {
      var sellingPlanRadio = section.querySelector('input[data-radio-type="selling_plan"]:checked');
      var isOneTime = section.querySelector('input[data-radio-type="one_time_purchase"]:checked');
      if (sellingPlanRadio && !isOneTime) {
        var planVal = sellingPlanRadio.value || sellingPlanRadio.getAttribute('data-selling-plan-id') || '';
        if (planVal && planVal !== '0') item.selling_plan = Number(planVal);
      }
    }
    return item;
  }

  /** Progressive backoff for cart-add retries (ms). A freshly created custom
   *  formula is a brand-new Shopify variant; it can take longer than ~10s to
   *  enter the storefront's buyable index, during which /cart/add.js returns
   *  422 "sold out". These delays cover that tail: ~1.5s initial + the sum
   *  below ≈ 31s total before giving up. */
  var CART_RETRY_DELAYS = [2000, 2500, 3000, 3500, 4000, 4500, 5000, 6000];

  /** Post items to /cart/add.js with retry on 422 (inventory propagation) and
   *  429 (rate limit). Used for single-section adds; the plan button uses
   *  planCartAddViaFetch instead. */
  function cartAddWithRetry(payload, attempt) {
    attempt = attempt || 0;
    return new Promise(function (resolve) {
      setTimeout(resolve, attempt === 0 ? 1500 : 0);
    }).then(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
    }).then(function (cartResp) {
      // 422 = variant still propagating to the storefront after Admin-API creation.
      if (cartResp.status === 422 && attempt < CART_RETRY_DELAYS.length) {
        var wait = CART_RETRY_DELAYS[attempt];
        console.warn('[quiz-results-page] Cart 422 (formula still propagating) — retry', attempt + 1, 'in', wait + 'ms');
        return new Promise(function (r) { setTimeout(r, wait); }).then(function () {
          return cartAddWithRetry(payload, attempt + 1);
        });
      }
      // 429 = Cloudflare/Shopify rate limit; back off harder.
      if (cartResp.status === 429 && attempt < CART_RETRY_DELAYS.length) {
        console.warn('[quiz-results-page] Cart 429 (rate limited) — retry', attempt + 1, 'in 15s');
        return new Promise(function (r) { setTimeout(r, 15000); }).then(function () {
          return cartAddWithRetry(payload, attempt + 1);
        });
      }
      return cartResp;
    });
  }

  /** Parse a /cart/add.js error response into a human-readable message */
  function parseCartError(status, txt) {
    try {
      var j = JSON.parse(txt);
      return j.description || j.message || ('Cart error (' + status + ')');
    } catch (e) {
      return 'Cart error (' + status + '): ' + txt.slice(0, 120);
    }
  }

  /** Add multiple cart items to the cart via fetch('/cart/add.js').
   *  Mirrors cartAddWithRetry: waits 1.5s before the first attempt, then
   *  retries on 422 (sold-out/propagation lag) up to 4 times at 2.5s intervals.
   *  Also retries on 429 (Cloudflare/Shopify rate limit) up to 3 times at 15s. */
  function planCartAddViaFetch(cartItems, attemptNum) {
    attemptNum = attemptNum || 0;
    var initialDelay = attemptNum === 0 ? 1500 : 0;
    console.log('[quiz-results-page] planCartAddViaFetch attempt', attemptNum, JSON.stringify(cartItems).slice(0, 200));
    return new Promise(function (resolve) { setTimeout(resolve, initialDelay); })
    .then(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: cartItems })
      });
    })
    .then(function (resp) {
      // Retry on 422 (inventory propagation / sold-out): new variants can take
      // 10-30s to become available in the Shopify storefront after Admin API
      // creation. Use the same progressive backoff as cartAddWithRetry.
      if (resp.status === 422 && attemptNum < CART_RETRY_DELAYS.length) {
        var wait = CART_RETRY_DELAYS[attemptNum];
        console.warn('[quiz-results-page] planCartAddViaFetch 422 (propagating) — retry', attemptNum + 1, 'in', wait + 'ms');
        return new Promise(function (r) { setTimeout(r, wait); }).then(function () {
          return planCartAddViaFetch(cartItems, attemptNum + 1);
        });
      }
      // Retry on 429 (Cloudflare / Shopify rate limit)
      if (resp.status === 429 && attemptNum < CART_RETRY_DELAYS.length) {
        console.warn('[quiz-results-page] planCartAddViaFetch 429 — retrying in 15s (attempt', attemptNum + 1, ')');
        return new Promise(function (r) { setTimeout(r, 15000); }).then(function () {
          return planCartAddViaFetch(cartItems, attemptNum + 1);
        });
      }
      if (!resp.ok) {
        return resp.text().then(function (txt) {
          throw new Error(parseCartError(resp.status, txt));
        });
      }
      return resp.json();
    });
  }

  /** Fallback: add multiple cart items via a real HTML form POST to /cart/add.
   *  Only used if fetch fails; bypasses Cloudflare bot detection as a last resort. */
  function planCartAddViaForm(cartItems) {
    var form = document.createElement('form');
    form.method = 'POST';
    form.action = '/cart/add';
    form.style.display = 'none';

    function addField(name, value) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    cartItems.forEach(function (item, i) {
      addField('items[' + i + '][id]', item.id);
      addField('items[' + i + '][quantity]', item.quantity || 1);
      if (item.selling_plan) {
        addField('items[' + i + '][selling_plan]', item.selling_plan);
      }
      var props = item.properties || {};
      Object.keys(props).forEach(function (key) {
        addField('items[' + i + '][properties][' + key + ']', props[key]);
      });
    });

    addField('return_to', '/cart');

    document.body.appendChild(form);
    form.submit();
  }

  /** Call the app proxy to create a custom variant and return the cart line-item object.
   *  Does NOT call /cart/add.js — used by the plan button to batch all adds into one request. */
  function createVariantForFormula(formula, formulaName, section, sellingPlanOverride) {
    var formData = buildCartPayload(formula, formulaName, section, sellingPlanOverride);
    var role = section.getAttribute('data-formula-role') || 'unknown';
    return fetch(APP_PROXY_ENDPOINT, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
    .then(function (resp) {
      if (!resp.ok) {
        return resp.text().then(function (txt) {
          throw new Error('Server error (' + resp.status + '): ' + txt.slice(0, 200));
        });
      }
      return resp.json();
    })
    .then(function (data) {
      if (!data.ok) throw new Error(data.message || 'Unable to create custom formula.');
      if (!data.variant_id) throw new Error('Missing variant ID from server.');
      console.log('[quiz-results-page] createVariantForFormula for', role, ':', data.variant_id);
      return buildCartItemFromServerData(data, section, sellingPlanOverride);
    });
  }

  /** Intercept a section's product form to go through app proxy */
  function wireFormToAppProxy(section, formula, formulaName) {
    if (!section || !formula) return;

    var productFormEl = section.querySelector('product-form');
    if (!productFormEl) return;

    var form = productFormEl.querySelector('form.js-product-form');
    if (!form) return;

    var submitBtn = productFormEl.querySelector('[name="add"], [data-add-to-cart]');

    // Remove the product-form custom element's submit handler by cloning
    // and add our own
    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      evt.stopImmediatePropagation();

      // Guard: if a price-refresh fetch is still in-flight, defer submission
      // until it completes so the cart always receives up-to-date pricing.
      var formRole = section.getAttribute('data-formula-role') || '';
      if (sectionPriceUpdating[formRole]) {
        console.log('[quiz-results-page] wireFormToAppProxy: price update in-flight for', formRole, '— deferring submit');
        var waitInterval = setInterval(function () {
          if (!sectionPriceUpdating[formRole]) {
            clearInterval(waitInterval);
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }, 50);
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
        submitBtn.textContent = 'Adding...';
      }

      addFormulaToCart(formula, formulaName, section)
        .then(function () {
          if (submitBtn) {
            submitBtn.textContent = 'Added!';
            submitBtn.classList.remove('is-loading');
            submitBtn.classList.add('is-success');
          }
          // Update cart icon bubble
          return fetch('/cart.js');
        })
        .then(function (resp) { return resp.json(); })
        .then(function (cart) {
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            var countEl = bubble.querySelector('[data-cart-count]') || bubble.querySelector('.cart-count');
            if (countEl) countEl.textContent = cart.item_count;
          }
          // Open cart drawer via theme's custom event system
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
          setTimeout(function () {
            if (submitBtn) {
              submitBtn.classList.remove('is-success');
              submitBtn.disabled = false;
              var origText = submitBtn.getAttribute('data-add-to-cart-text');
              submitBtn.textContent = origText || 'Add to Cart';
            }
          }, 2000);
        })
        .catch(function (err) {
          console.error('[quiz-results-page] Add to cart failed:', err);
          alert(err.message || 'Unable to add to cart. Please try again.');
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-loading');
            var origText = submitBtn.getAttribute('data-add-to-cart-text');
            submitBtn.textContent = origText || 'Add to Cart';
          }
        });
    }, true); // Use capture to fire before product-form's handler
  }

  /** Wire the plan's buy button to add all formulas to cart */
  function wirePlanBuyButton(planSection, results) {
    if (!planSection) return;

    var form = planSection.querySelector('form.js-product-form');
    if (!form) return;

    var submitBtn = planSection.querySelector('[name="add"], [data-add-to-cart]');

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      evt.stopImmediatePropagation();

      // Guard: if any formula section still has a price-refresh fetch in-flight,
      // defer submission until all updates settle.
      var planRoles = ['hydrationA', 'hydrationB', 'protein'];
      var anyUpdating = planRoles.some(function (r) { return !!sectionPriceUpdating[r]; });
      if (anyUpdating) {
        console.log('[quiz-results-page] wirePlanBuyButton: price update in-flight — deferring submit');
        var planWaitInterval = setInterval(function () {
          var stillUpdating = planRoles.some(function (r) { return !!sectionPriceUpdating[r]; });
          if (!stillUpdating) {
            clearInterval(planWaitInterval);
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }, 50);
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
        submitBtn.textContent = 'Adding formulas...';
      }

      // Read selling plan from the plan section's subscription widget
      var planSellingPlan = '';
      var planSubBlock = planSection.querySelector('section.shopify_subscriptions_app_block');
      if (planSubBlock) {
        var planSubRadio = planSubBlock.querySelector('input[data-radio-type="selling_plan"][data-selling-plan-id]:checked');
        var planOneTime = planSubBlock.querySelector('input[data-radio-type="one_time_purchase"]:checked');
        if (planSubRadio && !planOneTime) {
          planSellingPlan = planSubRadio.getAttribute('data-selling-plan-id') || planSubRadio.value || '';
          console.log('[quiz-results-page] wirePlanBuyButton: plan selling_plan =', planSellingPlan);
        }
      }

      // Build list of formulas to add
      var formulas = [];
      var haSec = document.querySelector('[data-formula-role="hydrationA"]');
      if (results.hydrationA && haSec) {
        formulas.push({ formula: results.hydrationA, name: results.formulaNames.a, section: haSec });
      }
      if (results.is3Plus && results.hydrationB) {
        var hbSec = document.querySelector('[data-formula-role="hydrationB"]');
        if (hbSec) {
          formulas.push({ formula: results.hydrationB, name: results.formulaNames.b, section: hbSec });
        }
      }
      var pSec = document.querySelector('[data-formula-role="protein"]');
      if (results.protein && pSec) {
        formulas.push({ formula: results.protein, name: results.formulaNames.protein, section: pSec });
      }

      // Create all variants in PARALLEL, then add ALL to cart in a single /cart/add.js call.
      if (submitBtn) submitBtn.textContent = 'Preparing formulas...';

      Promise.all(formulas.map(function (item) {
        return createVariantForFormula(item.formula, item.name, item.section, planSellingPlan);
      }))
      .then(function (cartItems) {
        if (submitBtn) submitBtn.textContent = 'Adding to cart...';
        // Use a real form POST to /cart/add — this is a genuine browser navigation
        // so it bypasses Cloudflare's AJAX cart protection. PHP waits for selling
        // plan allocations to propagate before returning, so the form submission
        // will find the variant fully ready for subscription purchase.
        planCartAddViaForm(cartItems);
      })
      .catch(function (err) {
        console.error('[quiz-results-page] Plan add to cart failed:', err);
        alert('Failed to add plan to cart: ' + (err.message || 'Unknown error'));
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-loading');
          var origText = submitBtn.getAttribute('data-add-to-cart-text');
          submitBtn.textContent = origText || 'Add The Plan To Cart';
        }
      });
    }, true);
  }

  /** Apply flavor selection to a product section's flavor radio inputs */
  function applyFlavorToSection(section, flavorName) {
    if (!section || !flavorName) return;
    var radios = section.querySelectorAll('input[name="properties[Flavor]"]');
    var target = flavorName.toLowerCase();
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value.toLowerCase() === target) {
        radios[i].checked = true;
        radios[i].dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /** Apply slider values within a section (hydration) */
  function applyHydrationSliders(section, formula) {
    if (!section || !formula) return;
    var sliderMap = {
      'flavor_slider': formula.flavor_slider,
      'electrolytes_slider': formula.electrolytes_slider,
      'calories_slider': formula.calories_slider,
      'carbs_slider': formula.carbs_slider,
      'aminoacids_slider': formula.aminoacids_slider,
      'caffeine_slider': formula.caffeine_slider,
      'protein_slider': formula.protein_slider
    };

    for (var id in sliderMap) {
      if (sliderMap[id] === undefined) continue;
      var slider = section.querySelector('#' + id) || section.querySelector('[data-slider="' + id + '"]');
      if (slider) {
        slider.value = sliderMap[id];
        slider.dispatchEvent(new Event('input', { bubbles: true }));
        slider.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Protein type
    if (formula.protein_type) {
      var ptRadios = section.querySelectorAll('input[name="properties[Protein Type]"]');
      var target = formula.protein_type.toLowerCase();
      for (var i = 0; i < ptRadios.length; i++) {
        if (ptRadios[i].value.toLowerCase() === target) {
          ptRadios[i].checked = true;
          ptRadios[i].dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    }
  }

  /** Apply slider values within a section (protein) */
  function applyProteinSliders(section, formula) {
    if (!section || !formula) return;

    // Calorie/carbs slider
    var ccSlider = section.querySelector('#calorie_carbs_slider') || section.querySelector('[data-slider="calorie_carbs_slider"]');
    if (ccSlider && formula.calorie_carbs_slider !== undefined) {
      ccSlider.value = formula.calorie_carbs_slider;
      ccSlider.dispatchEvent(new Event('input', { bubbles: true }));
      ccSlider.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Protein type
    if (formula.protein_type) {
      var ptRadios = section.querySelectorAll('input[name="properties[Protein Type]"]');
      var target = formula.protein_type.toLowerCase();
      for (var i = 0; i < ptRadios.length; i++) {
        if (ptRadios[i].value.toLowerCase() === target) {
          ptRadios[i].checked = true;
          ptRadios[i].dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    }
  }

  /** Populate a formula product section */
  function populateSection(section, role, results) {
    if (!section) return;

    var formula, formulaName, pageType;

    if (role === 'hydrationA') {
      formula = results.hydrationA;
      formulaName = results.formulaNames.a;
      pageType = 'hydration';
    } else if (role === 'hydrationB') {
      formula = results.hydrationB;
      formulaName = results.formulaNames.b;
      pageType = 'hydration';
    } else if (role === 'protein') {
      formula = results.protein;
      formulaName = results.formulaNames.protein;
      pageType = 'protein';
    }

    if (!formula) return;

    console.log('[quiz-results-page] populateSection', role, '| formula.boosts:', JSON.stringify(formula.boosts), '| formula.type:', formula.type, '| formula.flavor:', formula.flavor);

    // Formula name
    var nameEl = section.querySelector('[data-quiz-formula-name]');
    if (nameEl) {
      nameEl.innerHTML = '<h2>' + formulaName + '</h2>';
    }

    var summaryNameEl = section.querySelector('[data-quiz-formula-name-summary]');
    if (summaryNameEl) {
      summaryNameEl.textContent = formulaName;
    }

    // Flavor swatch
    var flavEl = section.querySelector('[data-quiz-formula-flav]');
    if (flavEl) {
      flavEl.innerHTML = flavorHTML(formula.flavor);
    }

    // Description (per-formula, with placeholder tokens — will be finalized when API data arrives)
    var descEl = section.querySelector('[data-quiz-formula-desc]');
    if (descEl) {
      var descText = null;
      if (role === 'hydrationA') descText = results.hydrationAText;
      else if (role === 'hydrationB') descText = results.hydrationBText;
      else if (role === 'protein') descText = results.proteinText;
      descEl.innerHTML = formulaDescHTML(descText || [], null);
    }

    // Apply flavor selection to the section's product form
    applyFlavorToSection(section, formula.flavor);

    // Apply sliders
    if (pageType === 'hydration') {
      applyHydrationSliders(section, formula);
    } else {
      applyProteinSliders(section, formula);
    }

    // Apply boosts (scoped to this section) — checks recommended, hides others.
    // Waits for infinit:boosts:rendered event, then applies state.
    applyBoostsScoped(section, formula.boosts);

    // Wire the buy button through the app proxy
    wireFormToAppProxy(section, formula, formulaName);

    // Update "Customize Your Formula" link with FormulaUrlHash
    if (window.FormulaUrlHash) {
      var encodableFormula = Object.assign({}, formula);
      var pkgVal = getSelectedPackageSize(section);
      if (pkgVal) encodableFormula.package_size = parseInt(pkgVal, 10);

      // Map quiz engine protein type names to builder page radio values.
      // The quiz engine uses short names ("Whey", "Vegan") for the backend API,
      // but the builder page radios use full names ("Whey Isolate", "Vegan Blend").
      var PROTEIN_TYPE_MAP = {
        'Whey': 'Whey Isolate',
        'Vegan': 'Vegan Blend'
      };
      if (encodableFormula.protein_type && PROTEIN_TYPE_MAP[encodableFormula.protein_type]) {
        encodableFormula.protein_type = PROTEIN_TYPE_MAP[encodableFormula.protein_type];
      }

      // Enforce correct type for the hash (safeguard against stale/wrong formula data)
      var expectedType = pageType === 'protein' ? 'p' : 'h';
      if (encodableFormula.type !== expectedType) {
        console.warn('[quiz-results-page] Formula type mismatch for', role, '| formula.type:', encodableFormula.type, '| expected:', expectedType, '| forcing correct type');
        encodableFormula.type = expectedType;
      }

      console.log('[quiz-results-page] Encoding hash for', role, '| encodable:', JSON.stringify(encodableFormula));
      var hash = window.FormulaUrlHash.encode(encodableFormula);
      console.log('[quiz-results-page] Encoded hash for', role, ':', hash ? atob(hash) : '(empty)');
      if (hash) {
        var basePath = pageType === 'protein' ? '/products/custom-protein-formula' : '/products/custom-hydration-formula';
        var linkSelector = pageType === 'protein'
          ? 'a[href*="custom-protein-formula"]'
          : 'a[href*="custom-hydration-formula"]';
        var links = section.querySelectorAll(linkSelector);
        console.log('[quiz-results-page] Setting hash on', links.length, 'links matching', linkSelector, 'in section', role);
        for (var i = 0; i < links.length; i++) {
          links[i].href = basePath + '#' + hash;
        }
      }
    }

    // Fetch price after boosts are rendered (event-driven, no arbitrary delay)
    waitForBoosts(section).then(function () {
      refreshSectionPrice(section, formula, role);
    });

    // Wire listeners for boost/subscription/variant changes to recalculate price
    wirePriceListeners(section, formula, role);

    var planSection = document.querySelector('[data-formula-role="the-plan"]');
    if (planSection) {
      var card = planSection.querySelector('[data-plan-card="' + role + '"]');
      var packagingEl = card && card.querySelector('[data-plan-packaging]');
      if (packagingEl) {
        setSkeletonText(packagingEl, packagingEl.textContent.trim());
      }
    }

    // Listen for variant-picker changes to update base price and plan card packaging label
    var variantPicker = section.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.addEventListener('on:variant:change', function (e) {
        var variant = e.detail && e.detail.variant;
        if (variant && variant.price) {
          section.setAttribute('data-base-price-cents', variant.price);
        }
        // Update the corresponding plan card's packaging label
        if (variant) {
          var planSection = document.querySelector('[data-formula-role="the-plan"]');
          if (planSection) {
            var card = planSection.querySelector('[data-plan-card="' + role + '"]');
            var packagingEl = card && card.querySelector('[data-plan-packaging]');
            if (packagingEl) {
              // variant.title is the full option value, e.g. "25 Serving Resealable Eco-bag"
              var label = (variant.option1 || variant.title || '').replace(/-/g, ' ');
              setSkeletonText(packagingEl, label);
            }
          }
        }
      });
    }
  }

  /** Populate a plan card in the-plan top section */
  function populatePlanCard(card, formula, formulaName) {
    if (!card || !formula) return;

    var nameEl = card.querySelector('[data-plan-name]');
    setSkeletonText(nameEl, formulaName);

    var flavorEl = card.querySelector('[data-plan-flavor]');
    if (flavorEl) {
      flavorEl.innerHTML = planFlavorHTML(formula.flavor);
      flavorEl.classList.remove('is-skeleton');
    }

    // Update protein type text
    var ptEl = card.querySelector('[data-plan-protein-type]');
    if (ptEl) {
      var ptSkeleton = ptEl.closest('.quiz-skeleton-text');
      var ptWrap = ptEl.closest('.quiz-result-plan_product-protein-source');

      if (formula.protein_type) {
        ptEl.textContent = formula.protein_type.toUpperCase();
        if (ptSkeleton) ptSkeleton.classList.remove('is-skeleton');
        if (ptWrap) ptWrap.style.display = '';
      } else if (formula.protein_slider !== undefined && formula.protein_slider === 0) {
        ptEl.textContent = '';
        if (ptSkeleton) ptSkeleton.classList.remove('is-skeleton');
        if (ptWrap) ptWrap.style.display = 'none';
      }
    }

    // Clear hardcoded placeholder boost text if formula has no recommended boosts
    var boostsEl = card.querySelector('[data-plan-boosts]');
    if (boostsEl && (!formula.boosts || !formula.boosts.length)) {
      boostsEl.classList.remove('is-skeleton');
      boostsEl.textContent = '';
      boostsEl.style.display = 'none';
    }
  }

  /** Update plan card boost names from checked checkboxes in the full sections */
  function updatePlanBoosts(planSection, results) {
    if (!planSection) return;

    var roles = ['hydrationA', 'hydrationB', 'protein'];
    roles.forEach(function (role) {
      var formula = role === 'hydrationA' ? results.hydrationA :
                    role === 'hydrationB' ? results.hydrationB :
                    results.protein;
      if (!formula || !formula.boosts || !formula.boosts.length) return;

      var card = planSection.querySelector('[data-plan-card="' + role + '"]');
      if (!card) return;

      var boostsEl = card.querySelector('[data-plan-boosts]');
      if (!boostsEl) return;

      var fullSection = document.querySelector('[data-formula-role="' + role + '"]');
      if (fullSection) {
        var names = getCheckedBoostNames(fullSection);
        if (names.length) {
          setSkeletonText(boostsEl, 'Boosts: ' + names.join(', '));
        } else {
          setSkeletonText(boostsEl, 'Boosts: ' + formula.boosts.length + ' included');
        }
      } else {
        setSkeletonText(boostsEl, 'Boosts: ' + formula.boosts.length + ' included');
      }
    });
  }

  /** Show a "no results" message */
  function showNoResults() {
    var planSection = document.querySelector('[data-formula-role="the-plan"]');
    if (!planSection) return;

    var heading = planSection.querySelector('[data-plan-heading]');
    if (heading) heading.textContent = 'YOUR CUSTOM FUELING PLAN';

    var mediaGallery = planSection.querySelector('.media-gallery');
    if (mediaGallery) {
      var notice = document.createElement('div');
      notice.className = 'p-6 text-center';
      notice.innerHTML = '<div class="rte">' +
        '<p>We don\'t have your quiz results yet.</p>' +
        '<p><a href="/pages/quiz" class="btn btn--primary">Take the Quiz</a></p>' +
        '</div>';
      // Insert at the top of the gallery, after the heading block
      var firstBlock = mediaGallery.querySelector('.p-6.border-bottom');
      if (firstBlock) {
        firstBlock.parentNode.insertBefore(notice, firstBlock);
      } else {
        mediaGallery.appendChild(notice);
      }
    }

    // Hide the product sections since there are no results
    document.querySelectorAll('[data-formula-role="hydrationA"], [data-formula-role="hydrationB"], [data-formula-role="protein"]').forEach(function (el) {
      var wrapper = el.closest('.shopify-section') || el.closest('section') || el;
      wrapper.style.display = 'none';
    });
  }

  /** Main initialization */
  function init(sessionData) {
    console.log('[quiz-results-page] init() called. sessionData:', sessionData ? 'present' : 'null/undefined');
    var results = sessionData && sessionData.results;
    // Store results globally so refreshSectionPrice can access per-formula text
    window._quizResults = results;
    if (results) {
      console.log('[quiz-results-page] results.protein:', JSON.stringify(results.protein));
      console.log('[quiz-results-page] results.hydrationA:', JSON.stringify(results.hydrationA));
    }
    if (!results) {
      showNoResults();
      return;
    }

    // ── The Plan (top summary section) ──
    var planSection = document.querySelector('[data-formula-role="the-plan"]');
    if (planSection) {
      // Update heading with first name
      var heading = planSection.querySelector('[data-plan-heading]');
      if (heading && results.firstName) {
        setSkeletonText(heading, results.firstName.toUpperCase() + "'S CUSTOM FUELING PLAN");
      }

      // Populate the 3 mini cards
      populatePlanCard(
        planSection.querySelector('[data-plan-card="hydrationA"]'),
        results.hydrationA,
        results.formulaNames.a
      );
      populatePlanCard(
        planSection.querySelector('[data-plan-card="hydrationB"]'),
        results.hydrationB,
        results.formulaNames.b
      );
      populatePlanCard(
        planSection.querySelector('[data-plan-card="protein"]'),
        results.protein,
        results.formulaNames.protein
      );

      // Hide hydration B card if not a 3+ hour workout
      if (!results.is3Plus) {
        var hbCard = planSection.querySelector('[data-plan-card="hydrationB"]');
        if (hbCard) hbCard.style.display = 'none';
      }

      // Wire the plan's buy button to add all formulas to cart
      wirePlanBuyButton(planSection, results);

      // Wire subscription widget changes in the plan section to update the total price display
      wirePlanSubscriptionListener(planSection);
    }

    // Track whether hydration B should be included in plan price
    includeHydrationB = !!results.is3Plus;

    // ── Full product sections ──
    var sections = document.querySelectorAll('[data-formula-role]');
    sections.forEach(function (section) {
      var role = section.getAttribute('data-formula-role');
      if (role === 'the-plan') return;
      // Skip hydrationB if not a 3+ hour workout
      if (role === 'hydrationB' && !results.is3Plus) return;

      populateSection(section, role, results);
    });

    // Hide hydration B full section if not 3+ hours
    if (!results.is3Plus) {
      var hbSection = document.querySelector('[data-formula-role="hydrationB"]');
      if (hbSection) {
        // Hide the section and its preceding spacer
        var sectionEl = hbSection.closest('.shopify-section') || hbSection.closest('section') || hbSection;
        sectionEl.style.display = 'none';
        // Also hide the spacer before it
        var prev = sectionEl.previousElementSibling;
        if (prev && prev.querySelector('[class*="spacer"], [data-desktop-height]')) {
          prev.style.display = 'none';
        }
      }
    }

    // Update plan boost names after a delay (give boost checkboxes time to load)
    if (planSection) {
      // Update plan boost names when each section's boosts finish rendering
      ['hydrationA', 'hydrationB', 'protein'].forEach(function (role) {
        var sec = document.querySelector('[data-formula-role="' + role + '"]');
        if (sec) {
          waitForBoosts(sec).then(function () {
            updatePlanBoosts(planSection, results);
          });
        }
      });

      // Also listen for boost checkbox changes to update plan card names reactively
      document.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || t.name !== 'boosts[]') return;
        var formulaSection = t.closest('[data-formula-role]');
        if (!formulaSection) return;
        var role = formulaSection.getAttribute('data-formula-role');
        if (role === 'the-plan') return;
        // Delay slightly to let applyBoostsScoped finish checking all boxes
        setTimeout(function () {
          var card = planSection.querySelector('[data-plan-card="' + role + '"]');
          var boostsEl = card && card.querySelector('[data-plan-boosts]');
          if (!boostsEl) return;
          var names = getCheckedBoostNames(formulaSection);
          if (names.length) {
            setSkeletonText(boostsEl, 'Boosts: ' + names.join(', '));
          }
        }, 300);
      }, true);
    }

    // Document-level listener for subscription widget changes.
    // The Shopify subscription widget may not bubble change events properly
    // from its radio inputs to the section element. Catch them here and
    // route to the appropriate section's cost-per-unit update.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;

      var isSubWidget = t.matches('section.shopify_subscriptions_app_block input[data-radio-type]') ||
                        t.matches('input[name="selling_plan"]') ||
                        t.matches('select[name="selling_plan"]');
      if (!isSubWidget) return;

      // Find the formula section this belongs to
      var formulaSection = t.closest('[data-formula-role]');
      if (!formulaSection) return;
      var sectionRole = formulaSection.getAttribute('data-formula-role');
      if (sectionRole === 'the-plan') return; // plan has its own listener

      console.log('[quiz-results-page] Document-level sub change for', sectionRole);

      if (sectionLastData[sectionRole]) {
        // Delay to let the widget update the hidden selling_plan input
        setTimeout(function () {
          updateSectionCostPerUnit(formulaSection, sectionLastData[sectionRole], sectionRole);
          applySubscriptionDiscountToMainPrice(formulaSection);
        }, 200);
      }
    }, true);
  }

  /** Read ?submission=<token> from URL if present (Klaviyo email deep link). */
  function getSubmissionTokenFromUrl() {
    try {
      var token = new URLSearchParams(window.location.search).get('submission') || '';
      // Tokens are 32-char lowercase hex; reject anything else so we
      // don't make a wasted request on user-typed garbage.
      return /^[a-f0-9]{32}$/.test(token) ? token : '';
    } catch (e) {
      return '';
    }
  }

  /** Fetch a prior submission from the backend (works without InfinitSession). */
  function loadResultsFromSubmissionToken(token) {
    var url = '/apps/formulas/quiz/get?submission=' + encodeURIComponent(token);
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (!json || !json.ok || !json.submission || !json.submission.payload) {
          throw new Error('Submission not found');
        }
        var p = json.submission.payload;
        return { answers: p.answers || {}, results: p.results || null, scored_at: p.scored_at || null };
      });
  }

  /** Wait for InfinitSession and load results */
  function waitForSession() {
    console.log('[quiz-results-page] waitForSession() called. InfinitSession exists:', !!window.InfinitSession, '| initialized:', !!(window.InfinitSession && window.InfinitSession.isInitialized()));

    // Deep-link path: ?submission=<token>. Bypass InfinitSession entirely
    // since this URL may be opened in a different browser/device than
    // the original quiz session (eg. from the Klaviyo email on phone).
    var token = getSubmissionTokenFromUrl();
    if (token) {
      loadResultsFromSubmissionToken(token).then(function (data) {
        // Hydrate session if available, so the rest of the page (which
        // reads from InfinitSession in several places) keeps working.
        if (window.InfinitSession && window.InfinitSession.isInitialized()) {
          try { window.InfinitSession.set('quiz_results', data); } catch (e) {}
        }
        init(data);
      }).catch(function (err) {
        console.error('[quiz-results-page] submission deep-link load failed:', err);
        showNoResults();
      });
      return;
    }

    // Check if already initialized
    if (window.InfinitSession && window.InfinitSession.isInitialized()) {
      loadResults();
      return;
    }

    // Listen for the ready event
    window.addEventListener('infinit:session:ready', function () {
      loadResults();
    }, { once: true });

    // Timeout fallback: if session never loads, show no-results
    setTimeout(function () {
      if (!window.InfinitSession || !window.InfinitSession.isInitialized()) {
        console.warn('[quiz-results-page] Session not available after timeout');
        showNoResults();
      }
    }, 8000);
  }

  function loadResults() {
    console.log('[quiz-results-page] loadResults() called');
    window.InfinitSession.get('quiz_results').then(function (data) {
      console.log('[quiz-results-page] Got quiz_results from session:', data ? 'present' : 'null', '| has results:', !!(data && data.results));
      init(data);
    }).catch(function (err) {
      console.error('[quiz-results-page] Failed to load quiz results:', err);
      showNoResults();
    });
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForSession);
  } else {
    waitForSession();
  }
})();